import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { fetchPublicNews, type PublicNewsItem } from "@/features/public/api";
import { useRealtimeInvalidation } from "@/shared/realtime/useRealtimeInvalidation";

const defaultArticleImage =
  "https://readdy.ai/api/search-image?query=senegal%20africa%20news%20article%20professional%20photography%20editorial%20clean%20background&width=400&height=300&seq=home-news&orientation=landscape";

export default function NewsSection() {
  const [visible, setVisible] = useState(false);
  const [articles, setArticles] = useState<PublicNewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const ref = useRef<HTMLDivElement>(null);

  const loadNews = useCallback(async () => {
    setLoading(true);

    try {
      const data = await fetchPublicNews({ page: 1, limit: 4 });
      setArticles(data.items);
    } catch {
      setArticles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    void loadNews();
  }, [loadNews]);

  useRealtimeInvalidation({
    topics: ["news"],
    onInvalidate: () => {
      void loadNews();
    }
  });

  const featured = articles.find((news) => news.featured) ?? articles[0];
  const others = articles.filter((news) => news.id !== featured?.id).slice(0, 3);

  return (
    <section ref={ref} className="py-24 bg-white">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className={`flex flex-col sm:flex-row items-start sm:items-center justify-between mb-12 gap-4 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div>
            <span className="inline-block px-4 py-1.5 rounded-full bg-orange-50 text-[#E8521A] text-sm font-medium mb-3">
              Actualités
            </span>
            <h2 className="text-4xl font-bold text-[#1A1A1A]">Dernières Actualités</h2>
          </div>
          <Link
            to="/actualites"
            className="inline-flex items-center gap-2 text-sm font-medium text-[#E8521A] hover:gap-3 transition-all whitespace-nowrap"
          >
            Toutes les actualités
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Featured */}
          {loading ? (
            <div className="rounded-3xl border border-gray-100 bg-gray-50 h-[420px] animate-pulse"></div>
          ) : featured ? (
            <Link
              to={`/actualites/${featured.id}`}
              className={`group rounded-3xl overflow-hidden border-2 border-gray-200 hover:-translate-y-1 transition-all duration-300 cursor-pointer ${
                visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
            >
              <div className="relative h-64 overflow-hidden">
                <img
                  src={featured.image ?? defaultArticleImage}
                  alt={featured.titre}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1.5 rounded-full bg-[#E8521A] text-white text-sm font-bold">
                    {featured.categorie}
                  </span>
                </div>
              </div>
              <div className="p-8">
                <div className="flex items-center gap-2 text-sm text-gray-400 mb-3">
                  <i className="ri-calendar-line"></i>
                  <span>{new Date(featured.date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</span>
                </div>
                <h3 className="text-2xl font-bold text-[#1A1A1A] mb-3 group-hover:text-[#E8521A] transition-colors leading-snug">
                  {featured.titre}
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed mb-4 line-clamp-3">{featured.extrait}</p>
                <span className="inline-flex items-center gap-1 text-sm font-medium text-[#E8521A] hover:gap-2 transition-all">
                  Lire la suite
                  <i className="ri-arrow-right-line"></i>
                </span>
              </div>
            </Link>
          ) : (
            <div className="rounded-3xl border border-gray-100 bg-white p-8 text-sm text-gray-500">
              Aucune actualité publiée pour le moment.
            </div>
          )}

          {/* Others */}
          <div className="space-y-4">
            {loading
              ? Array.from({ length: 3 }).map((_, index) => (
                  <div key={index} className="h-28 rounded-2xl bg-gray-50 border border-gray-100 animate-pulse"></div>
                ))
              : others.map((news, i) => (
              <Link
                to={`/actualites/${news.id}`}
                key={news.id}
                className={`group flex gap-4 p-4 rounded-2xl border-2 border-gray-200 hover:border-[#E8521A]/30 hover:-translate-y-0.5 transition-all duration-200 cursor-pointer ${
                  visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                }`}
                style={{ transitionDelay: `${(i + 1) * 100}ms` }}
              >
                <div className="w-24 h-24 flex-shrink-0 rounded-xl overflow-hidden">
                  <img
                    src={news.image ?? defaultArticleImage}
                    alt={news.titre}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-medium text-[#E8521A] bg-orange-50 px-2 py-0.5 rounded-full">
                      {news.categorie}
                    </span>
                    <span className="text-sm text-gray-400">
                      {new Date(news.date).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}
                    </span>
                  </div>
                  <h4 className="font-bold text-[#1A1A1A] text-sm leading-snug mb-1 group-hover:text-[#E8521A] transition-colors line-clamp-2">
                    {news.titre}
                  </h4>
                  <p className="text-sm text-gray-400 line-clamp-2">{news.extrait}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
